import { useState, useRef, useEffect } from 'react';
import { Play, Pause, Headphones } from 'lucide-react';

const cardStyle = {
  backgroundColor: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
};

// The voice a member last picked, remembered on this device only — a
// convenience, so failures (private mode) just fall back to the first voice.
const VOICE_KEY = 'relax.voice';
const readVoice = () => { try { return localStorage.getItem(VOICE_KEY); } catch { return null; } };
const writeVoice = (v) => { try { localStorage.setItem(VOICE_KEY, v); } catch { /* private mode */ } };

const fmt = (s) => {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

// One guided-audio session: play/pause, a seekable progress bar and a voice
// choice. Unmounting (leaving Relax) stops playback with the <audio> element.
const RelaxAudioCard = ({ session }) => {
  const audioRef = useRef(null);
  const [voiceId, setVoiceId] = useState(() => {
    const saved = readVoice();
    return session.voices.some((v) => v.id === saved) ? saved : session.voices[0].id;
  });
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(session.minutes * 60);
  const voice = session.voices.find((v) => v.id === voiceId);

  // Lock-screen / control-centre title while it plays.
  useEffect(() => {
    if (!playing || !('mediaSession' in navigator) || typeof window.MediaMetadata !== 'function') return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: session.title,
        artist: 'DSP Cafe',
        artwork: [{ src: '/logo512.png', sizes: '512x512', type: 'image/png' }],
      });
    } catch { /* unsupported */ }
  }, [playing, session.title]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play().catch(() => setPlaying(false));
    else a.pause();
  };

  const chooseVoice = (id) => {
    if (id === voiceId) return;
    audioRef.current?.pause();
    setPlaying(false);
    setCurrent(0);
    setVoiceId(id);
    writeVoice(id);
  };

  const seek = (e) => {
    const a = audioRef.current;
    const t = Number(e.target.value);
    if (a) a.currentTime = t;
    setCurrent(t);
  };

  return (
    <div className="rounded-2xl p-4" style={cardStyle}>
      <audio
        ref={audioRef}
        src={voice.src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setCurrent(0); }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => { if (Number.isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); }}
      />

      <div className="flex items-center gap-3.5">
        <button
          onClick={toggle}
          aria-label={playing ? `Pause ${session.title}` : `Play ${session.title}`}
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-white transition-all hover:brightness-110 active:scale-95"
          style={{ backgroundColor: 'var(--brand-primary)' }}
        >
          {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
        </button>
        <div className="min-w-0 flex-1">
          <h3 className="font-bold text-[15px] leading-snug" style={{ color: 'var(--text-primary)' }}>{session.title}</h3>
          <p className="flex items-center gap-1.5 text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            <Headphones size={13} aria-hidden="true" />
            Guided audio · {session.minutes} min · headphones recommended
          </p>
        </div>
      </div>

      <div className="mt-3.5">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={1}
          value={Math.min(current, duration || 0)}
          onChange={seek}
          aria-label="Position"
          aria-valuetext={`${fmt(current)} of ${fmt(duration)}`}
          className="w-full h-1.5 cursor-pointer"
          style={{ accentColor: 'var(--brand-primary)' }}
        />
        <div className="flex justify-between text-xs mt-1 tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
          <span>{fmt(current)}</span>
          <span>-{fmt(Math.max(0, duration - current))}</span>
        </div>
      </div>

      <div className="flex gap-2 mt-2" role="group" aria-label="Voice">
        {session.voices.map((v) => {
          const on = v.id === voiceId;
          return (
            <button
              key={v.id}
              onClick={() => chooseVoice(v.id)}
              aria-pressed={on}
              className="text-xs font-semibold rounded-full px-3 py-1.5 transition-all active:scale-95"
              style={on
                ? { backgroundColor: 'var(--brand-primary)', color: '#fff', border: '1px solid var(--brand-primary)' }
                : { backgroundColor: 'var(--pill-bg)', color: 'var(--text-secondary)', border: '1px solid var(--pill-border)' }}
            >
              {v.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default RelaxAudioCard;
